import { useEffect, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import { registerNavigator } from '@/lib/navigation'
import { ease } from '@/lib/motion'
import { useGlobalShortcuts } from '@/hooks/useGlobalShortcuts'
import { MOBILE_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { useApplyTheme } from '@/hooks/useTheme'
import { useUiStore } from '@/stores/uiStore'
import { Sheet } from '@/components/ui/Dialog'
import { Sidebar } from './Sidebar'

const SIDEBAR_WIDTH = 284

/** Polite live region: announces completed answers, never individual tokens. */
function LiveRegion() {
  const { text, nonce } = useUiStore((s) => s.announcement)
  return (
    <div aria-live="polite" aria-atomic="true" className="sr-only">
      {text ? <span key={nonce}>{text}</span> : null}
    </div>
  )
}

/** Lets the streaming controller navigate (/ → /c/:id) through the router. */
function NavigationBridge() {
  const navigate = useNavigate()
  useEffect(() => {
    registerNavigator((to, options) => navigate(to, options))
    return () => registerNavigator(null)
  }, [navigate])
  return null
}

/** App frame: sidebar (persistent across pages) and the page area. */
export function AppShell({ children }: { children: ReactNode }) {
  useApplyTheme()
  useGlobalShortcuts()
  const isMobile = useMediaQuery(MOBILE_QUERY)
  const collapsed = useUiStore((s) => s.sidebarCollapsed)
  const drawerOpen = useUiStore((s) => s.mobileSidebarOpen)
  const setDrawerOpen = useUiStore((s) => s.setMobileSidebarOpen)

  useEffect(() => {
    if (!isMobile && drawerOpen) setDrawerOpen(false)
  }, [isMobile, drawerOpen, setDrawerOpen])

  return (
    <div className="flex h-dvh overflow-hidden bg-canvas text-ink">
      <a
        href="#main"
        className="sr-only z-50 rounded-full bg-accent px-4 py-2 text-sm font-medium text-accent-ink focus:not-sr-only focus:absolute focus:left-3 focus:top-3"
      >
        Skip to content
      </a>

      {isMobile ? (
        <Sheet
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          side="left"
          title="Menu"
          showClose={false}
          className="w-[min(300px,88vw)]"
        >
          <Sidebar inDrawer />
        </Sheet>
      ) : (
        <AnimatePresence initial={false}>
          {!collapsed && (
            <motion.aside
              key="sidebar"
              aria-label="Sidebar"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: SIDEBAR_WIDTH, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              // A short eased tween: the main column re-flows on every frame of this, and a
              // spring's long settling tail would mean many extra tiny re-flows.
              transition={{ duration: 0.32, ease: ease.apple }}
              className="relative z-30 flex shrink-0 overflow-hidden bg-panel shadow-[inset_-0.5px_0_0_rgb(var(--ink)/0.08)]"
            >
              <div className="flex h-full" style={{ width: SIDEBAR_WIDTH }}>
                <Sidebar />
              </div>
            </motion.aside>
          )}
        </AnimatePresence>
      )}

      <div className="relative flex min-w-0 flex-1">{children}</div>

      <LiveRegion />
      <NavigationBridge />
    </div>
  )
}
